import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Calendar, Plus, Video, Sparkles, User as UserIcon } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * KidsTutoring — home page for the kid-safe tutoring feature.
 *
 * Parents + admins can book a session. Kid accounts can see their own
 * upcoming rooms. Every room is a thin wrapper over the existing
 * /meetings collection (kind="kids_tutor") so the underlying WebRTC
 * mesh signaling is identical to the rest of the platform — only the
 * surrounding UI is kid-branded.
 */
export default function KidsTutoring() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Math Tutoring");
  const [subject, setSubject] = useState("math");
  const [tutorEmail, setTutorEmail] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/kids-tutor/rooms");
      setRooms(r.data?.rooms || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const create = async () => {
    if (!title.trim() || !subject.trim()) { toast.error("Add a title and subject"); return; }
    setCreating(true);
    try {
      const r = await api.post("/kids-tutor/rooms", {
        title: title.trim(),
        subject: subject.trim(),
        tutor_email: tutorEmail.trim() || null,
        scheduled_for: scheduledFor || null,
      });
      toast.success("Tutoring room created! 🎉");
      setOpen(false);
      load();
      if (r.data?.id) nav(`/kids/tutoring/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  if (!user) {
    return (
      <div data-testid="kids-tutor-login-required" className="pt-32 px-6 text-center text-white">
        <GraduationCap className="w-10 h-10 text-pink-400 mx-auto mb-3" />
        <div className="font-display text-2xl">Sign in to book tutoring</div>
        <Link to="/login" className="inline-block mt-4 bg-yellow-300 text-black font-bold px-5 py-2 rounded-full">Sign in</Link>
      </div>
    );
  }

  const canBook = user.is_parent || user.role === "admin" || user.is_kids_safe;

  return (
    <div data-testid="kids-tutoring-page" className="pb-20 px-4 sm:px-8 md:px-12 max-w-6xl mx-auto text-white">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#ff61a6] via-[#8b5cf6] to-[#22d3ee] p-6 md:p-10 shadow-2xl">
        <div className="absolute -top-10 -right-10 text-[10rem] opacity-20 pointer-events-none select-none">🎓</div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur text-white text-xs uppercase tracking-[0.28em] font-black px-3 py-1 rounded-full mb-3">
            <Sparkles className="w-3 h-3" /> Tap IN Kids · Tutoring
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter drop-shadow">Learn 1-on-1 with a grown-up tutor.</h1>
          <p className="text-white/85 mt-2 max-w-2xl">
            Safe, parent-approved video sessions for homework, reading, music, and more. Same super-smooth video your grown-ups use — just kid-branded and private.
          </p>
          {canBook && (
            <button
              data-testid="kids-tutor-book-btn"
              onClick={() => setOpen(true)}
              className="mt-5 inline-flex items-center gap-2 bg-yellow-300 hover:bg-yellow-200 text-black font-black px-5 py-3 rounded-full shadow-lg"
            ><Plus className="w-5 h-5" /> Book a tutoring session</button>
          )}
        </div>
      </div>

      <section data-testid="kids-tutor-rooms" className="mt-8">
        <h2 className="font-display font-black text-2xl mb-3 inline-flex items-center gap-2"><Calendar className="w-6 h-6 text-pink-400" /> Your tutoring rooms</h2>
        {loading && <div className="text-white/60 text-sm">Loading…</div>}
        {!loading && rooms.length === 0 && (
          <div data-testid="kids-tutor-empty" className="bg-black/30 border border-dashed border-white/10 rounded-2xl p-8 text-center">
            <div className="text-5xl mb-2">🎓</div>
            <div className="text-white font-bold mb-1">No sessions yet</div>
            <div className="text-white/60 text-sm">Book your first tutoring session above.</div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rooms.map((r) => (
            <Link
              key={r.id}
              to={`/kids/tutoring/${r.id}`}
              data-testid={`kids-tutor-room-${r.id}`}
              className="bg-gradient-to-br from-fuchsia-500/15 to-sky-500/15 border border-white/10 rounded-2xl p-4 hover:ring-2 hover:ring-pink-400 transition"
            >
              <div className="flex items-center gap-2 text-yellow-300 text-[10px] uppercase tracking-[0.25em] font-black">
                <GraduationCap className="w-3 h-3" /> {r.kids_tutor?.subject || "tutoring"}
              </div>
              <div className="font-display font-black text-lg mt-1 line-clamp-2">{r.title}</div>
              <div className="text-white/60 text-xs mt-1 inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> Hosted by {r.host_name}</div>
              {r.scheduled_for && (
                <div className="text-white/70 text-xs mt-1">🗓️ {new Date(r.scheduled_for).toLocaleString()}</div>
              )}
              <div className="mt-3 inline-flex items-center gap-1 bg-white/10 text-white font-bold text-xs px-3 py-1.5 rounded-full">
                <Video className="w-3 h-3" /> Join room
              </div>
            </Link>
          ))}
        </div>
      </section>

      {open && (
        <div data-testid="kids-tutor-modal" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-xl text-[#222] mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-fuchsia-500" /> Book tutoring</h3>
            <label className="block text-xs font-bold text-[#444] mb-1">What are we working on?</label>
            <input data-testid="kids-tutor-title" value={title} onChange={(e) => setTitle(e.target.value.slice(0, 120))} className="w-full bg-[#f6f6f8] border border-[#ddd] rounded-lg px-3 py-2 text-[#222] text-sm mb-3" />
            <label className="block text-xs font-bold text-[#444] mb-1">Subject</label>
            <select data-testid="kids-tutor-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-[#f6f6f8] border border-[#ddd] rounded-lg px-3 py-2 text-[#222] text-sm mb-3">
              {["math","reading","science","music","art","language","coding","homework"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="block text-xs font-bold text-[#444] mb-1">Tutor's email <span className="font-normal text-[#888]">(they'll get the join link)</span></label>
            <input data-testid="kids-tutor-tutor-email" type="email" value={tutorEmail} onChange={(e) => setTutorEmail(e.target.value.slice(0, 120))} placeholder="teacher@…" className="w-full bg-[#f6f6f8] border border-[#ddd] rounded-lg px-3 py-2 text-[#222] text-sm mb-3" />
            <label className="block text-xs font-bold text-[#444] mb-1">When? (optional)</label>
            <input data-testid="kids-tutor-when" type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} className="w-full bg-[#f6f6f8] border border-[#ddd] rounded-lg px-3 py-2 text-[#222] text-sm mb-4" />
            <div className="flex items-center gap-2">
              <button data-testid="kids-tutor-create" onClick={create} disabled={creating} className="flex-1 bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-400 hover:to-pink-400 text-white font-black px-4 py-2.5 rounded-full disabled:opacity-60">{creating ? "Creating…" : "Create room 🎉"}</button>
              <button onClick={() => setOpen(false)} className="bg-[#f0f0f3] hover:bg-[#e6e6eb] text-[#333] font-bold px-4 py-2.5 rounded-full">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
